import {
  CohortResponseDuration,
  CohortResponseEndDate,
  CohortResponseStartDate,
  CohortResponseTimeCommitment,
  CohortType,
  SAPApplicationParameters,
} from '@platform/client/schemas';
import { Button, HelpIcon, Logo, PillBadge } from '@platform/components';
import { ApplicationStorageDataKeys } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { AuthedPage } from '../../../app';
import { ROUTES } from '../../../routes/consts';
import { navigate } from 'wouter/use-browser-location';
import { Application } from '@platform/client';
import Skeleton from 'react-loading-skeleton';
import { ReactNode } from 'react';
import { ApplicationsClosedContent } from '../shared/ApplicationsClosed';
import { ReviewModeStorage, REVIEW_MODE_STORAGE_KEY } from '../../../dashboard/home/RejoinPanel';

const dateOptions = {
  month: 'short',
  day: 'numeric',
} as Intl.DateTimeFormatOptions;

export const SapCohortSelectionPage = () => {
  const { getApplicationDataByKey: getReviewModeData } =
    useApplicationLocalStorage<ReviewModeStorage>(REVIEW_MODE_STORAGE_KEY);
  const isRejoin = getReviewModeData('reviewMode');
  const nextUrl =
    isRejoin ?
      `/platform/welcome-back/sap`
    : `/platform/auth${ROUTES.APPLICATION_START}?partner=sap`;

  const { updateApplicationData } = useApplicationLocalStorage<SAPApplicationParameters>(
    ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION,
  );
  const { data: cohortsData, isSuccess, isLoading } = Application.useGetAvailableCohortsSap();
  return (
    <AuthedPage>
      <div className='flex flex-col overflow-x-clip overflow-y-auto h-[100vh] bg-gray-50'>
        <div className='flex-grow w-full py-2 px-3 text-gray-600'>
          <div className='w-full p-4'>
            <Logo type='mw-text' variant='blue' />
          </div>
          <div className='w-full md:w-[700px] flex flex-col gap-8 mx-auto'>
            <div>
              <h2 className='text-display-sm font-medium mb-4'>
                Choose how you want to participate
              </h2>
              <h4 className='text-md'>
                Choose the cohort that fits your availability, skills, and interests.
              </h4>
            </div>
            {isLoading && <Skeleton height={200} width={'100%'} className='mb-4' />}
            {isLoading && <Skeleton height={200} width={'100%'} className='mb-4' />}
            {isSuccess &&
              cohortsData.length > 0 &&
              cohortsData.map((cohort) => (
                <CohortCard
                  key={cohort.cohortId}
                  name={cohort.displayName}
                  supportText={cohort.description}
                  startDate={cohort.startDate}
                  endDate={cohort.endDate}
                  durationText={cohort.duration}
                  timeCommitmentText={cohort.timeCommitment}
                  isTeam={cohort.type === CohortType.Teams}
                  onClick={() => {
                    updateApplicationData({
                      cohortId: cohort.cohortId,
                      teamSelection: cohort.type === CohortType.Teams ? 'team' : 'individual',
                    });
                    navigate(nextUrl);
                  }}
                  isClosed={cohort.isClosed}
                />
              ))}
            {isSuccess && cohortsData.length === 0 && (
              <ApplicationsClosedContent programName='SAP Acceleration Collective' />
            )}
          </div>
        </div>
      </div>
    </AuthedPage>
  );
};

const CohortCard = ({
  name,
  supportText,
  startDate,
  endDate,
  durationText,
  timeCommitmentText,
  isTeam,
  onClick,
  isClosed = true,
}: {
  name: string;
  supportText: string;
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
  durationText: CohortResponseDuration;
  timeCommitmentText: CohortResponseTimeCommitment;
  isTeam: boolean;
  onClick: () => void;
  isClosed?: boolean;
}) => {
  return (
    <div className='bg-white flex flex-col gap-6 shadow-xs rounded-xl border border-gray-100 p-6 w-full'>
      <CohortHeaderSection cohortName={name} supportText={supportText} isClosed={isClosed} />
      <CohortDetailsSection
        startDate={startDate}
        endDate={endDate}
        durationText={durationText}
        timeCommitmentText={timeCommitmentText}
        isTeam={isTeam}
      />
      <Button
        variant='primary'
        size='sm'
        additionalClassnames='w-fit'
        onPress={onClick}
        isDisabled={isClosed}
      >
        {!isClosed ? 'Apply to this cohort' : 'Application closed'}
      </Button>
    </div>
  );
};

const CohortHeaderSection = ({
  cohortName,
  supportText,
  isClosed = true,
}: {
  cohortName: string;
  supportText: string;
  isClosed?: boolean;
}) => {
  return (
    <div>
      <div className='flex items-center justify-between'>
        <h3 className='text-xl font-medium'>{cohortName}</h3>
        {!isClosed && (
          <PillBadge color='success-25' variant='dot' size='sm'>
            Open
          </PillBadge>
        )}
        {isClosed && (
          <PillBadge color='orange' variant='dot' size='sm'>
            Full
          </PillBadge>
        )}
      </div>
      <p className='text-sm text-gray-500 mt-1'>{supportText}</p>
    </div>
  );
};

const CohortDetailsSection = ({
  startDate,
  endDate,
  durationText,
  timeCommitmentText,
  isTeam,
}: {
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
  durationText: CohortResponseDuration;
  timeCommitmentText: CohortResponseTimeCommitment;
  isTeam: boolean;
}) => {
  return (
    <div className='flex w-full gap-20'>
      <CohortDatesSection startDate={startDate} endDate={endDate} />
      <CohortDetail label='Duration' value={durationText} />
      <CohortDetail label='Time Commitment' value={timeCommitmentText} />
      <CohortFormatSection isTeam={isTeam} />
    </div>
  );
};

const CohortDatesSection = ({
  startDate,
  endDate,
}: {
  startDate: CohortResponseStartDate;
  endDate: CohortResponseEndDate;
}) => {
  if (!startDate || !endDate) return null;
  const datesText = `${new Date(startDate).toLocaleDateString('en-US', dateOptions)} - ${new Date(
    endDate,
  ).toLocaleDateString('en-US', dateOptions)}`;
  return <CohortDetail label='Dates' value={datesText} />;
};

const CohortFormatSection = ({ isTeam }: { isTeam: boolean }) => {
  const tooltipText =
    isTeam ?
      `You'll work with a team of 3-4 professionals supporting one social enterprise together`
    : `You'll work independently with one social enterprise`;
  return (
    <CohortDetail
      label='Format'
      value={
        <p className='flex gap-1 items-center'>
          <span>{isTeam ? 'Team' : 'Individual'}</span>
          <HelpIcon supportingText={tooltipText} size='xs' maxWidth='180px' />
        </p>
      }
    />
  );
};

const CohortDetail = ({ label, value }: { label: string; value?: string | ReactNode | null }) => {
  if (!value) return null;
  return (
    <div className='flex flex-col gap-1'>
      <p className='text-sm text-gray-400'>{label}</p>
      <p className='text-sm text-gray-500'>{value}</p>
    </div>
  );
};
